import type { CSSProperties } from "react";
import { formatCurrency } from "@/utils/format";
import type { ProgressiveRaterStatus } from "@/api/types";

const MODE_LABELS: Record<string, string> = {
  mock: "Simulated",
  live: "Live carrier",
  live_sandbox: "Sandbox (simulated)",
};

const MODE_CLASS: Record<string, string> = {
  mock: "rater-mode-mock",
  live: "rater-mode-live",
  live_sandbox: "rater-mode-sandbox",
};

export function progressiveModeLabel(mode: string): string {
  return MODE_LABELS[mode] ?? mode;
}

export function ProgressiveModeBadge({ mode }: { mode: string }) {
  return <span className={`rater-mode-badge ${MODE_CLASS[mode] ?? ""}`}>{progressiveModeLabel(mode)}</span>;
}

// Canonical disclaimer — keep in sync with backend labeling.SIMULATED_PREMIUM_NOTICE.
export const SIMULATED_PREMIUM_NOTICE = "Simulated premium — not a carrier quote";

export function SimulatedBadge() {
  return (
    <span
      title={SIMULATED_PREMIUM_NOTICE}
      style={{
        display: "inline-block",
        fontSize: "0.7rem",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.03em",
        padding: "0.1rem 0.4rem",
        borderRadius: 6,
        color: "#92400e",
        background: "#fef3c7",
        border: "1px solid #fcd34d",
      }}
    >
      Simulated
    </span>
  );
}

export function SimulatedPremiumNotice({ style }: { style?: CSSProperties }) {
  return (
    <div
      role="note"
      style={{
        display: "flex",
        gap: "0.6rem",
        alignItems: "flex-start",
        padding: "0.75rem 1rem",
        borderRadius: 10,
        background: "#fffbeb",
        border: "1px solid #fcd34d",
        color: "#92400e",
        fontSize: "0.9rem",
        ...style,
      }}
    >
      <span aria-hidden style={{ fontSize: "1rem", lineHeight: 1.2 }}>⚠️</span>
      <span>
        <strong>Simulated premiums — not carrier quotes.</strong> These figures come from
        demonstration raters, are indicative only, and are not bindable. Only carriers in
        <strong> Live carrier</strong> mode return real quotes.
      </span>
    </div>
  );
}

export function ProgressiveRaterPanel({
  status,
  testing,
  testResult,
  onTest,
  compact = false,
}: {
  status: ProgressiveRaterStatus | null;
  testing: boolean;
  testResult: string | null;
  onTest: () => void;
  compact?: boolean;
}) {
  if (!status) return null;

  return (
    <div className={`card progressive-rater-panel${compact ? " compact" : ""}`} style={{ padding: "1rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1rem" }}>
        <div>
          <h3 style={{ margin: 0 }}>Progressive rater</h3>
          <div style={{ marginTop: "0.35rem" }}>
            <ProgressiveModeBadge mode={status.mode} />
            {status.ready ? (
              <span className="rater-ready-pill">Ready</span>
            ) : (
              <span className="rater-not-ready-pill">Not ready</span>
            )}
          </div>
        </div>
        <button type="button" className="btn btn-secondary" onClick={onTest} disabled={testing || !status.enabled}>
          {testing ? "Testing…" : "Test connection"}
        </button>
      </div>
      <p style={{ margin: "0.75rem 0 0", color: "var(--muted)", fontSize: "0.9rem" }}>{status.message}</p>
      {!compact ? (
        <div className="detail-grid" style={{ marginTop: "0.75rem" }}>
          <div className="detail-item">
            <div className="label">Live enabled</div>
            <div className="value">{status.enabled ? "Yes" : "No"}</div>
          </div>
          <div className="detail-item">
            <div className="label">API URL</div>
            <div className="value">{status.api_url_configured ? "Configured" : "—"}</div>
          </div>
          <div className="detail-item">
            <div className="label">API key</div>
            <div className="value">{status.api_key_configured ? "Configured" : "—"}</div>
          </div>
          <div className="detail-item">
            <div className="label">Agency code</div>
            <div className="value">{status.agency_code_configured ? "Configured" : "—"}</div>
          </div>
        </div>
      ) : null}
      {testResult ? (
        <div
          className="card"
          style={{
            marginTop: "0.75rem",
            padding: "0.75rem",
            background: testResult.startsWith("✓") ? "var(--accent-soft)" : "#fef2f2",
          }}
        >
          {testResult}
        </div>
      ) : null}
    </div>
  );
}

export function formatProgressiveTestResult(result: {
  success: boolean;
  mode: string;
  adapter: string;
  latency_ms: number;
  quote_reference: string | null;
  premium_cents: number | null;
  message: string;
  error: string | null;
}): string {
  if (!result.success) {
    return `✗ ${result.error ?? result.message}`;
  }
  const premium = result.premium_cents != null ? formatCurrency(result.premium_cents) : "—";
  return `✓ ${result.message} (${result.adapter}, ${result.latency_ms}ms, ${premium}, ref ${result.quote_reference ?? "—"})`;
}
